/**
 * The team dashboard calls (openspec cs-team-dashboards). No company and no caller are sent: the
 * server reads both from the token. `employeeId` is whose dashboard is open (omitted: your own).
 */
import mainApi from '@/api/axios';
import { API_ENDPOINTS } from '@/api/endpoints';
import type { PickupItem, StatusItem, VesselItem } from '../types/customerService';
import type {
  CountsView,
  Directory,
  SearchHit,
  StatusView,
  TeamScope,
  TeamView,
} from '../types/teamDashboard';

const ENDPOINTS = API_ENDPOINTS.TEAM_DASHBOARD;

const payload = <T>(body: unknown): T | undefined => {
  const envelope = body as { Data1?: T; data1?: T } | null;
  return envelope?.Data1 ?? envelope?.data1;
};

const get = async <T>(url: string, params: Record<string, unknown>): Promise<T | undefined> => {
  const { data } = await mainApi.get(url, { params });
  return payload<T>(data);
};

const list = async <T>(url: string, params: Record<string, unknown>): Promise<T[]> => {
  const value = await get<T[]>(url, params);
  return Array.isArray(value) ? value : [];
};

/** The query parameters naming whose dashboard and which scope; nothing for your own "My data". */
export const whoParams = (employeeId: number | null, scope: TeamScope): Record<string, unknown> => ({
  ...(employeeId ? { employeeId } : {}),
  ...(scope === 'team' ? { scope: 'team' } : {}),
});

const EMPTY_COUNTS: CountsView = {
  total: { total: 0, billed: 0, unbilled: 0, unbilledSince: 0 },
  rows: [],
};

export const teamDashboardApi = {
  getView: async (employeeId: number | null): Promise<TeamView | undefined> =>
    get<TeamView>(ENDPOINTS.VIEW, whoParams(employeeId, 'me')),

  getDirectory: async (department: string): Promise<Directory> =>
    (await get<Directory>(ENDPOINTS.DIRECTORY, { department })) ?? { top: [], unplaced: [] },

  search: async (text: string): Promise<SearchHit[]> => list<SearchHit>(ENDPOINTS.SEARCH, { text }),

  getPickups: async (
    employeeId: number | null,
    scope: TeamScope,
    fromDate: string,
    toDate: string,
  ): Promise<PickupItem[]> => list<PickupItem>(ENDPOINTS.PICKUPS, { ...whoParams(employeeId, scope), fromDate, toDate }),

  getVessels: async (
    employeeId: number | null,
    scope: TeamScope,
    fromDate: string,
    toDate: string,
  ): Promise<VesselItem[]> =>
    list<VesselItem>(ENDPOINTS.VESSELS, { ...whoParams(employeeId, scope), fromDate, toDate, etaType: 0 }),

  getJobCounts: async (
    employeeId: number | null,
    scope: TeamScope,
    fromDate: string,
    toDate: string,
    since: string,
  ): Promise<CountsView> =>
    (await get<CountsView>(ENDPOINTS.JOB_COUNTS, { ...whoParams(employeeId, scope), fromDate, toDate, since }))
    ?? EMPTY_COUNTS,

  getStatus: async (employeeId: number | null, scope: TeamScope): Promise<StatusView> => {
    const view = await get<StatusView>(ENDPOINTS.STATUS_SUMMARY, whoParams(employeeId, scope));
    return { total: (view?.total ?? []) as StatusItem[], groups: view?.groups ?? [] };
  },
};
